import React, { useEffect, useRef } from 'react';
import { Box, Divider, Grid, Typography } from '@mui/material';
import { ITransaction, TransactionTypeTranslations } from 'common/transactions.interface';
import { useSelector } from 'react-redux';
import { IRootState } from 'stores';
import { BankingState } from 'stores/banking';
import { IAccount } from 'common/accounts.interface';
import { format as formatDate } from 'date-fns';
import { formatNumberToBRL, formatTaxNumber } from 'utils/formatters';
import html2pdf from 'html2pdf.js';
import { useSnackbar } from 'notistack';
import LogoCelcoin from 'assets/images/logo_celcoin_branco.png';

type Props = {
  open: boolean;
  transaction?: ITransaction;
  onClose?: () => void;
};

const ProofOfPaymentModal = ({ open, onClose, transaction }: Props) => {
  const { accounts } = useSelector<IRootState, BankingState>(({ banking }) => banking);

  const [accountForTransaction, setAccountForTransaction] = React.useState<IAccount | undefined>();

  const { enqueueSnackbar } = useSnackbar();
  const contentRef = useRef<HTMLDivElement>(null);
  const generatingRef = useRef(false);

  useEffect(() => {
    if (!transaction) return;

    const account = accounts.find((acc) => acc.id === transaction.accountId);
    if (account) setAccountForTransaction(account);
  }, [transaction, accounts]);

  const formatDateTime = (value: string) => {
    const date = new Date(value);
    return formatDate(date, "dd/MM/yyyy 'às' HH:mm:ss");
  };

  const getDocumentLabel = (document?: string) => {
    if (!document) return '';
    return document.length === 11 ? 'CPF' : 'CNPJ';
  };

  const generatePdf = async () => {
    if (!contentRef.current) return null;

    const opt = {
      filename: 'comprovante-transacao.pdf',
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, letterRendering: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const },
    };

    return html2pdf().set(opt).from(contentRef.current).outputPdf('blob');
  };

  // ? Generates the pdf and triggers the download when the modal is opened and all data is ready
  useEffect(() => {
    const run = async () => {
      if (!open) return;
      if (!transaction) return;
      if (!accountForTransaction) return;
      if (generatingRef.current) return;

      generatingRef.current = true;

      const { fonts } = document as any;

      if (fonts?.ready) {
        await fonts.ready;
      }

      // eslint-disable-next-line no-promise-executor-return
      await new Promise((resolve) => requestAnimationFrame(resolve));

      const blob = await generatePdf();

      if (blob) {
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = 'Comprovante transação.pdf';
        link.click();

        URL.revokeObjectURL(url);

        enqueueSnackbar('Comprovante gerado com sucesso!', { variant: 'success' });
      }

      generatingRef.current = false;

      onClose?.();
    };

    run();
  }, [open, transaction, accountForTransaction]);

  if (!transaction || !accountForTransaction) return null;

  const holder = {
    name: accountForTransaction.businessName,
    taxNumber: accountForTransaction.cnpj,
    bankCode: accountForTransaction.bankCode,
    bankBranch: accountForTransaction.bankBranch,
    bankAccount: accountForTransaction.bankAccount,
  };

  const counterparty = {
    name: transaction.counterpartyName,
    taxNumber: transaction.counterpartyTaxNumber,
    bankCode: transaction.counterpartyBankCode,
    bankBranch: transaction.counterpartyBankBranch,
    bankAccount: transaction.counterpartyAccount,
    pixkey: transaction.counterpartyPixKey,
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: '-10000px',
        backgroundColor: '#fff',
      }}
    >
      <Box
        ref={contentRef}
        sx={{
          width: '794px',
          height: '1122px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          '&, & *': {
            fontFamily: '"Segoe UI", Roboto, Helvetica, Arial, sans-serif !important',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            backgroundColor: '#806DFB',
            px: '40px',
            py: '30px',
            color: '#EDE9FF',
            mb: 3,
          }}
        >
          <Box
            component="img"
            src={LogoCelcoin}
            alt="Torqi Logo"
            sx={{
              height: 50,
              display: 'block',
            }}
          />
        </Box>

        <Grid container px={4}>
          <Grid item xs={12} mb={3}>
            <Typography variant="h4" fontWeight={600} mb={1} align="center">
              Comprovante de Transação
            </Typography>
            <Typography variant="body1" fontWeight={500} color="#4E4D4D" align="center">
              {TransactionTypeTranslations[transaction.type]}
            </Typography>
          </Grid>

          <Grid item xs={12} mb={3}>
            <Box
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                p: 2,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Typography variant="body1" fontWeight={600} fontSize={18}>
                Valor: {formatNumberToBRL(transaction.value)}
              </Typography>
            </Box>
          </Grid>

          <Typography variant="body1" fontWeight={600} px={2}>
            Titular da conta
          </Typography>

          <Grid item xs={12} px={2}>
            <Typography fontWeight={500}>{holder.name}</Typography>
            <Typography>
              {getDocumentLabel(holder.taxNumber)}: {formatTaxNumber(holder.taxNumber)}
            </Typography>
            <Typography>{`Banco: ${holder.bankCode} | Ag: ${holder.bankBranch} | Conta: ${holder.bankAccount}`}</Typography>
          </Grid>

          <Grid item xs={12} px={2} my={2}>
            <Divider />
          </Grid>

          <Typography variant="body1" fontWeight={600} px={2}>
            Contraparte
          </Typography>

          <Grid item xs={12} px={2}>
            <Typography fontWeight={500}>{counterparty.name}</Typography>
            <Typography>
              {getDocumentLabel(counterparty.taxNumber)}: {formatTaxNumber(counterparty.taxNumber)}
            </Typography>

            {counterparty.bankCode && counterparty.bankBranch && counterparty.bankAccount ? (
              <Typography>
                {`Banco: ${counterparty.bankCode} | Ag: ${counterparty.bankBranch} | Conta: ${counterparty.bankAccount}`}
              </Typography>
            ) : (
              <Typography>{`Chave PIX: ${counterparty.pixkey}`}</Typography>
            )}
          </Grid>

          <Grid item xs={12} px={2} my={2}>
            <Divider />
          </Grid>

          <Grid item xs={12} px={2} mb={1}>
            <Typography component="span" fontWeight={500}>
              Descrição:
            </Typography>{' '}
            {transaction.description || '-'}
          </Grid>

          <Grid item xs={12} px={2} mb={1}>
            <Typography component="span" fontWeight={500}>
              Data/Hora da transação:
            </Typography>{' '}
            {formatDateTime(transaction.createdAt)}
          </Grid>

          <Grid item xs={12} px={2}>
            <Typography component="span" fontWeight={500}>
              Identificador da transação:
            </Typography>{' '}
            {transaction.endToEndId}
          </Grid>
        </Grid>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            backgroundColor: '#806DFB',
            px: '40px',
            py: '30px',
            color: '#EDE9FF',
          }}
        >
          <Typography fontSize={16}>
            Powered by{' '}
            <Box component="span" sx={{ fontWeight: 700 }}>
              Torqi
            </Box>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ProofOfPaymentModal;
